import React from "react";
import { useLocation } from "react-router-dom";

import {
  CardGridSkeleton,
  CommandCenterSkeleton,
  CyclesPageSkeleton,
  DashboardSkeleton,
  DefectDetailSkeleton,
  DefectPageSkeleton,
  DetailPageSkeleton,
  ElementsPageSkeleton,
  GuideMeSkeleton,
  PluginsSkeleton,
  ProjectsSkeleton,
  ReportsSkeleton,
  RequirementsSkeleton,
  TablePageNoActionSkeleton,
  TablePageSkeleton,
  TestCaseDetailSkeleton,
  TestCaseResultSkeleton,
  TestCasesListSkeleton,
  TestDataSkeleton,
  TestSuiteSkeleton,
  UploadsPageSkeleton,
  UsersSkeleton,
} from "./skeletons";

function getSkeletonForPath(pathname: string, search: string) {
  // Dashboard
  if (pathname === "/") return <DashboardSkeleton />;

  // Command Center dashboard (sidebar + KPI grid + burndown/forecast layout)
  if (pathname === "/dashboard" || pathname === "/dashboard/")
    return <CommandCenterSkeleton />;

  // Test Cases — list page (tree sidebar + "select a case" center)
  if (/^\/testcases\/(all|automation|manual|performance)$/.test(pathname))
    return <TestCasesListSkeleton />;

  // Elements — 3-pane page (Screens tree + element table)
  if (pathname === "/elements" || pathname === "/elements/")
    return <ElementsPageSkeleton />;

  // Uploads — 3-pane page (Files sidebar + preview + details panel)
  if (pathname === "/uploads" || pathname === "/uploads/")
    return <UploadsPageSkeleton />;

  // Test Cases — run results page
  if (/^\/testcases\/\d+\/test-results(\/\d+)?$/.test(pathname))
    return <TestCaseResultSkeleton />;

  // Cycles — list / detail (release tree + cycle workspace + context panel)
  if (pathname === "/cycles" || pathname === "/cycles/")
    return <CyclesPageSkeleton />;

  // Cycle — per-case run result page (3-pane: case list + steps + detail)
  if (/^\/cycles\/\d+\/results\/\d+\/cases\/\d+\/\d+$/.test(pathname))
    return <TestCaseResultSkeleton />;

  // Test Cases — detail page: clean /:id, /:id/testpage/:type, or
  // /type/:id/testpage/:type — tree sidebar + steps workspace + context panel
  if (
    /^\/testcases\/(?:(?:all|automation|manual|performance)\/)?\d+(?:\/testpage\/[^/]+)?$/.test(
      pathname,
    )
  )
    return <TestCaseDetailSkeleton />;

  // Test Cases — any other numeric detail sub-page
  if (/^\/testcases\/\d+\//.test(pathname)) return <TestCaseDetailSkeleton />;

  // Defects — detail view: URL uses ?id=<n> query param (not a path segment)
  if (pathname === "/defects" || pathname === "/defects/") {
    const hasId = /[?&]id=\d+/.test(search);
    return hasId ? <DefectDetailSkeleton /> : <DefectPageSkeleton />;
  }

  // Defects — path-based detail (legacy, kept for safety)
  if (/^\/defects\/\d+/.test(pathname)) return <DefectDetailSkeleton />;

  // Test Plans — card grid
  if (pathname === "/test-management/test-plans") return <CardGridSkeleton />;

  // Requirements
  if (pathname === "/test-management/requirements")
    return <RequirementsSkeleton />;

  // Test Runs
  if (pathname === "/test-management/test-runs")
    return <TablePageNoActionSkeleton />;

  // Test Suites list
  if (pathname === "/test-management/test-suites") return <TestSuiteSkeleton />;

  // Test Plan detail / results
  if (/^\/test-management\/test-plans\/\d+/.test(pathname))
    return <DetailPageSkeleton />;

  // Test Suite detail
  if (/^\/test-management\/test-suites\/\d+/.test(pathname))
    return <DetailPageSkeleton />;

  // Plugins — card grid with 5 tabs
  if (pathname === "/settings/plugins") return <PluginsSkeleton />;

  // Settings list pages
  if (pathname === "/settings/projects") return <ProjectsSkeleton />;
  if (pathname === "/org-admin/users") return <UsersSkeleton />;

  // Test Data
  if (pathname === "/test-data" || pathname === "/test-data/")
    return <TestDataSkeleton />;

  // Reports
  if (pathname === "/reports" || pathname === "/reports/")
    return <ReportsSkeleton />;

  // Settings detail (project detail, profile)
  if (
    /^\/settings\/projects\/\d+/.test(pathname) ||
    pathname === "/settings/my-profile"
  )
    return <DetailPageSkeleton />;

  // Guide Me — 3-pane (library sidebar + tour content + tour details)
  if (pathname === "/guideme" || pathname === "/guideme/")
    return <GuideMeSkeleton />;

  // Default: table page (Requirements, Test Suites, Test Runs, Reports, Settings, Test Data, etc.)
  return <TablePageSkeleton />;
}

const ContentLoader: React.FC = () => {
  const { pathname, search } = useLocation();
  return getSkeletonForPath(pathname, search);
};

export default ContentLoader;
